import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  Select,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Move } from 'lucide-react';

import { $bulkMoveFiles } from '../../_state/fileActions.state';
import { $showBulkMoveDialog } from '../../_state/fileDialogs.state';
import {
  $files,
  $selectedFileIds,
  $clearSelection,
} from '../../_state/files.state';
import { $directoriesById } from '../../_state/fileTree.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FileBulkMoveDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showBulkMoveDialog);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const files = useAtomValue($files);
  const { data: directoriesById } = useAtomValue($directoriesById);
  const bulkMove = useSetAtom($bulkMoveFiles);
  const clearSelection = useSetAtom($clearSelection);

  const selectedFiles = files.filter((f) => selectedFileIds.has(f.id));
  const folderCount = selectedFiles.filter(
    (f) => f.type === 'Directory',
  ).length;
  const fileCount = selectedFiles.filter((f) => f.type === 'Regular').length;

  const [targetFolderId, setTargetFolderId] = React.useState<string>('root');
  const [isMoving, setIsMoving] = React.useState(false);

  // Get all folders excluding selected folders and their descendants
  const availableFolders = React.useMemo(() => {
    if (!directoriesById) return [];

    const folders = Array.from(directoriesById.values());
    const selectedFolderIds = new Set(
      selectedFiles.filter((f) => f.type === 'Directory').map((f) => f.id),
    );

    // Exclude selected folders and their descendants
    return folders.filter((folder) => {
      // Exclude if this folder is selected
      if (selectedFolderIds.has(folder.id)) return false;

      // Exclude if this folder is a descendant of any selected folder
      return !folder.path.some((pathId) => selectedFolderIds.has(pathId));
    });
  }, [directoriesById, selectedFiles]);

  const handleMove = async () => {
    if (selectedFileIds.size === 0) {
      toast.error(t.dialogs.bulkMove.errorNoFiles);
      return;
    }

    const newParentId = targetFolderId === 'root' ? null : targetFolderId;

    setIsMoving(true);

    try {
      const result = await bulkMove({
        fileIds: Array.from(selectedFileIds),
        newParentId,
      });

      const targetName =
        targetFolderId === 'root'
          ? t.dialogs.bulkMove.optionRoot
          : directoriesById?.get(targetFolderId)?.name || '';

      if (result.successCount > 0) {
        const successMsg =
          result.successCount !== 1
            ? t.dialogs.bulkMove.successMovedPlural
            : t.dialogs.bulkMove.successMoved;
        toast.success(
          successMsg
            .replace('{count}', result.successCount.toString())
            .replace('{destination}', targetName),
        );
      }
      if (result.failedCount > 0) {
        const failedMsg =
          result.failedCount !== 1
            ? t.dialogs.bulkMove.errorFailedPlural
            : t.dialogs.bulkMove.errorFailed;
        toast.error(
          failedMsg.replace('{count}', result.failedCount.toString()),
        );
      }

      clearSelection();
      setIsOpen(false);
      setTargetFolderId('root');
    } catch (error) {
      toast.error(
        t.dialogs.bulkMove.errorGeneral.replace(
          '{error}',
          error instanceof Error ? error.message : '',
        ),
      );
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTargetFolderId('root');
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileBulkMoveDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Move className="size-5" />
            {selectedFileIds.size === 1
              ? t.dialogs.bulkMove.title.replace(
                  '{count}',
                  selectedFileIds.size.toString(),
                )
              : t.dialogs.bulkMove.titlePlural.replace(
                  '{count}',
                  selectedFileIds.size.toString(),
                )}
            ?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <p className="text-sm text-neutral/70">
            {selectedFileIds.size === 1
              ? t.dialogs.bulkMove.moving.replace(
                  '{count}',
                  selectedFileIds.size.toString(),
                )
              : t.dialogs.bulkMove.movingPlural.replace(
                  '{count}',
                  selectedFileIds.size.toString(),
                )}
          </p>

          <ul className="text-sm text-neutral/60">
            {fileCount > 0 && (
              <li>
                {fileCount === 1
                  ? t.dialogs.bulkMove.fileCount.replace(
                      '{count}',
                      fileCount.toString(),
                    )
                  : t.dialogs.bulkMove.fileCountPlural.replace(
                      '{count}',
                      fileCount.toString(),
                    )}
              </li>
            )}
            {folderCount > 0 && (
              <li>
                {folderCount === 1
                  ? t.dialogs.bulkMove.folderCount.replace(
                      '{count}',
                      folderCount.toString(),
                    )
                  : t.dialogs.bulkMove.folderCountPlural.replace(
                      '{count}',
                      folderCount.toString(),
                    )}
              </li>
            )}
          </ul>

          <Select
            label={t.dialogs.bulkMove.labelDestination}
            value={targetFolderId}
            onChange={(key) => setTargetFolderId(key)}
            options={[
              { key: 'root', label: t.dialogs.bulkMove.optionRoot },
              ...availableFolders
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((folder) => ({
                  key: folder.id,
                  label:
                    folder.path.length > 0
                      ? `${folder.path.map((id) => directoriesById?.get(id)?.name || id).join(' / ')} / ${folder.name}`
                      : folder.name,
                })),
            ]}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onPress={handleClose} isDisabled={isMoving}>
            {t.dialogs.bulkMove.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleMove}
            isDisabled={isMoving}
            isLoading={isMoving}
          >
            {t.dialogs.bulkMove.move}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
